import { Injectable } from '@angular/core';

/**
 * Shared service for computing icon/text contrast colours against
 * user-chosen entity background colours.
 *
 * Entity colours (team colour, skill colour, …) are picked by the user
 * and do NOT change between light/dark themes. The contrast colours
 * returned by this service reference theme-agnostic CSS custom properties
 * (`--icon-color-on-light` / `--icon-color-on-dark`) defined in
 * `variables.scss` so that they can be overridden centrally without
 * touching component code.
 */
@Injectable({ providedIn: 'root' })
export class ColorContrastService {
  /** CSS value for icons on a light entity surface. */
  private readonly onLight = 'var(--icon-color-on-light)';

  /** CSS value for icons on a dark entity surface. */
  private readonly onDark = 'var(--icon-color-on-dark)';

  /**
   * Return a CSS colour value (variable reference) that provides good
   * contrast when placed on top of `backgroundColor`.
   *
   * - `null` / `undefined` → fallback dark colour (assumes the caller
   *   applies a primary-gradient background via a CSS fallback class).
   * - Non-hex string → dark colour (safe default).
   * - Valid 6-digit hex → luminance-based decision.
   */
  getIconColor(backgroundColor: string | null | undefined): string {
    if (!backgroundColor) {
      return this.onDark;
    }

    if (!/^#[\dA-Fa-f]{6}$/.test(backgroundColor)) {
      return this.onLight;
    }

    return this.isColorLight(backgroundColor) ? this.onLight : this.onDark;
  }

  /**
   * Return `true` when the given hex colour is perceptually "light"
   * (relative luminance > 0.4).
   *
   * Uses the sRGB → linear-RGB conversion mandated by WCAG 2.x for
   * relative-luminance calculations.
   */
  isColorLight(hexColor: string): boolean {
    const hex = hexColor.replace('#', '');
    const r = Number.parseInt(hex.substring(0, 2), 16);
    const g = Number.parseInt(hex.substring(2, 4), 16);
    const b = Number.parseInt(hex.substring(4, 6), 16);

    const toLinear = (channel: number): number => {
      const srgb = channel / 255;
      return srgb <= 0.03928 ? srgb / 12.92 : Math.pow((srgb + 0.055) / 1.055, 2.4);
    };

    const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
    return luminance > 0.4;
  }
}
